import { colors } from '@lib/colors/colors'
import type { CircleLayer, SymbolLayer } from 'mapbox-gl'

/**
 * Unique Mapbox source identifier.
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/} for more details on sources.
 */
export const MAP_SOURCE_ID = 'trials' as const

/**
 * Unique layer IDs for various map visualizations.
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/} for layer definitions.
 */
export const MAP_LAYER_IDS = {
  CLUSTER_COUNT: 'cluster-count',
  CLUSTERS: 'clusters',
  UNCLUSTERED_LABEL: 'unclustered-label',
  UNCLUSTERED_POINT: 'unclustered-point',
} as const

/**
 * Layer configuration for clusters (circles representing groups of points).
 * Uses a "case" expression to dynamically adjust the circle color based on `point_count` and feature state.
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/} for details on expressions.
 */
export const clusterLayer: CircleLayer = {
  filter: ['has', 'point_count'],
  id: MAP_LAYER_IDS.CLUSTERS,
  paint: {
    'circle-color': [
      'case',
      ['boolean', ['feature-state', 'clicked'], false],
      // When clicked, use a darker version
      colors.primary800,
      // Otherwise, use the step expression based on point_count
      [
        'step',
        ['get', 'point_count'],
        colors.primary500, // for point_count < 10
        10,
        colors.primary500, // for point_count between 10 and 30
        30,
        colors.primary800, // for point_count >= 30
      ],
    ],
    'circle-radius': 15,
    'circle-stroke-color': colors.primary300,
    'circle-stroke-width': 2,
  },
  source: MAP_SOURCE_ID,
  type: 'circle',
}

/**
 * Layer configuration for displaying cluster count labels.
 * Uses a symbol layer to render abbreviated point counts.
 *
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#symbol-layout} for layout options.
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#symbol-paint} for paint options.
 */
export const clusterCountLayer: SymbolLayer = {
  filter: ['has', 'point_count'],
  id: MAP_LAYER_IDS.CLUSTER_COUNT,
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['Manrope Bold', 'sans-serif'],
    'text-size': 18,
  },
  paint: {
    'text-color': colors.white,
  },
  source: MAP_SOURCE_ID,
  type: 'symbol',
}

/**
 * Layer configuration for unclustered individual points.
 * The filter ensures only points without a cluster count are included.
 *
 * @see {@link https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#boolean} for filter expressions.
 */
export const unclusteredPointLayer: CircleLayer = {
  filter: ['!', ['has', 'point_count']],
  id: MAP_LAYER_IDS.UNCLUSTERED_POINT,
  paint: {
    'circle-color': [
      'case',
      ['boolean', ['feature-state', 'clicked'], false],
      // Use dark color when clicked
      colors.primary800,
      // Default color otherwise
      colors.primary500,
    ],
    'circle-radius': 15,
    'circle-stroke-color': colors.white,
    'circle-stroke-width': 1.5,
  },
  source: MAP_SOURCE_ID,
  type: 'circle',
}

/**
 * Layer configuration for labels on unclustered points.
 * Similar to `clusterCountLayer` but for individual points.
 * For more styling details, refer to the symbol layer documentation.
 */
export const unclusteredLabelLayer: SymbolLayer = {
  filter: ['!', ['has', 'point_count']],
  id: MAP_LAYER_IDS.UNCLUSTERED_LABEL,
  layout: {
    'text-field': '1',
    'text-font': ['Manrope Bold', 'sans-serif'],
    'text-size': 18,
  },
  paint: {
    'text-color': colors.white,
  },
  source: MAP_SOURCE_ID,
  type: 'symbol',
}
